import React from 'react';
import styled, { css } from 'styled-components';

// Glassmorphism Bento Card (matching Profile styles)
const CardContainer = styled.div`
  background: ${props => props.theme.colors.cardBg};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
              box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  
  /* Top shine line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    opacity: 0.5;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  
  /* Accent color indicator */
  ${props => props.$accentColor && css`
    &::after {
      content: '';
      position: absolute;
      top: 8px;
      left: 8px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${props.$accentColor};
      opacity: 0.8;
    }
  `}
  
  @media (max-width: 600px) {
    padding: 10px;
    border-radius: 12px;
  }
`;

const Value = styled.div`
  font-size: ${props => props.$large ? '2rem' : props.$medium ? '1.5rem' : '1.25rem'};
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.1;
  margin-bottom: 2px;
  
  @media (max-width: 600px) {
    font-size: ${props => props.$large ? '1.5rem' : props.$medium ? '1.25rem' : '1rem'};
  }
`;

const Label = styled.div`
  font-size: ${props => props.$large ? '13px' : '11px'};
  font-weight: 500;
  color: ${props => props.theme.colors.textSecondary};
  letter-spacing: 0.3px;
  
  @media (max-width: 600px) {
    font-size: ${props => props.$large ? '11px' : '10px'};
  }
`;

const Title = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  opacity: 0.7;
  
  @media (max-width: 600px) {
    font-size: 10px;
    margin-bottom: 4px;
  }
`;

const Trend = styled.div`
  font-size: ${props => props.$large ? '12px' : '10px'};
  margin-top: 4px;
  font-weight: 500;
  color: ${props => 
    props.$positive ? '#10b981' : 
    props.$negative ? '#ef4444' : 
    props.theme.colors.textSecondary};
    
  @media (max-width: 600px) {
    font-size: 9px;
    margin-top: 2px;
  }
`;

/**
 * StatCard Component - Glassmorphism Bento Style (matching Profile)
 * 
 * @param {string} iconColor - Accent color shown as a dot indicator
 * @param {string} title - Optional header title (uppercase, muted)
 * @param {string|number} value - Main value to display
 * @param {string} label - Label below the value
 * @param {string} trend - Trend text (e.g., "+12.5%")
 * @param {boolean} trendPositive - If true, trend is green
 * @param {boolean} trendNegative - If true, trend is red
 * @param {string} variant - Size: 'large', 'medium', or default
 * @param {string} className - Additional CSS class (for grid placement like bento-large)
 */
const StatCard = ({
  iconColor,
  title,
  value,
  label,
  trend,
  trendPositive,
  trendNegative,
  variant,
  className
}) => {
  const isLarge = variant === 'large';
  const isMedium = variant === 'medium';
  
  return (
    <CardContainer $accentColor={iconColor} className={className}>
      {title && <Title>{title}</Title>}
      <Value $large={isLarge} $medium={isMedium}>{value}</Value>
      {label && <Label $large={isLarge}>{label}</Label>}
      {trend && (
        <Trend $positive={trendPositive} $negative={trendNegative} $large={isLarge}>
          {trend}
        </Trend>
      )}
    </CardContainer>
  );
};

export default StatCard;
